import { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Upload, Loader2, Search, ArrowUp, ArrowDown, ListOrdered, Star } from "lucide-react";
import ModifierGroupsEditor from "./ModifierGroupsEditor";
import PlatterConfigEditor from "./PlatterConfigEditor";

const empty = {
  name: "", description: "", price: 0, category: "Signature Rolls",
  image_url: "", is_available: true, stock: 0, is_featured: false, contains_raw_seafood: false,
  display_order: 0, modifier_groups: [], platter_config: null,
};

import { ImageLightbox } from "@/components/ImageLightbox";

export default function MenuAdmin() {
  const [preview, setPreview] = useState(null);
  const [items, setItems] = useState([]);
  const [categoryOrder, setCategoryOrder] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [uploading, setUploading] = useState(false);
  const [q, setQ] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("");
  const [catEditorOpen, setCatEditorOpen] = useState(false);
  const [catOrderDraft, setCatOrderDraft] = useState([]);
  const fileInputRef = useRef(null);

  const load = () => Promise.all([
    api.get("/menu").then((r) => setItems(r.data)),
    api.get("/menu/category-order").then((r) => setCategoryOrder(r.data.order || [])).catch(() => {}),
  ]);
  useEffect(() => { load(); }, []);

  const categories = useMemo(
    () => Array.from(new Set(items.map((i) => i.category))),
    [items]
  );

  const orderedCategories = useMemo(() => {
    const seen = new Set(categories);
    const known = categoryOrder.filter((c) => seen.has(c));
    const rest = categories.filter((c) => !known.includes(c));
    return [...known, ...rest];
  }, [categories, categoryOrder]);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    return items.filter((i) =>
      (!categoryFilter || i.category === categoryFilter) &&
      (!s || i.name.toLowerCase().includes(s) || (i.description || "").toLowerCase().includes(s) || i.category.toLowerCase().includes(s))
    );
  }, [items, q, categoryFilter]);

  const save = async () => {
    try {
      const body = {
        ...form,
        price: parseFloat(form.price),
        stock: parseInt(form.stock || 0),
        display_order: parseInt(form.display_order || 0),
        modifier_groups: (form.modifier_groups || []).map((g) => ({
          name: g.name || "",
          type: g.type || "add",
          required: !!g.required,
          multi: g.multi !== false,
          options: (g.options || []).filter((o) => o.name?.trim()).map((o) => ({ name: o.name.trim() })),
        })).filter((g) => g.name.trim() && g.options.length > 0),
        platter_config: form.category === "Party Platters" ? form.platter_config : null,
      };
      if (editing) await api.put(`/menu/${editing.id}`, body);
      else await api.post("/menu", body);
      toast.success(editing ? "Item updated" : "Item created");
      setOpen(false); setEditing(null); setForm(empty);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const openEdit = (it) => {
    setEditing(it);
    setForm({
      name: it.name, description: it.description, price: it.price,
      category: it.category, image_url: it.image_url,
      is_available: it.is_available, stock: it.stock,
      is_featured: !!it.is_featured,
      contains_raw_seafood: !!it.contains_raw_seafood,
      display_order: it.display_order ?? 0,
      modifier_groups: it.modifier_groups || [],
      platter_config: it.platter_config || null,
    });
    setOpen(true);
  };
  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/menu/${id}`); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith("image/")) { toast.error("Please choose an image file."); return; }
    if (file.size > 8 * 1024 * 1024) { toast.error("Image is larger than 8 MB."); return; }
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload/menu-image", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, image_url: data.url }));
      toast.success("Photo uploaded");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  // ------- Item reorder -------
  const moveItem = async (item, direction) => {
    // Only reorder within the same category (as shown on customer menu)
    const siblings = items
      .filter((i) => i.category === item.category)
      .slice()
      .sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0) || a.name.localeCompare(b.name));
    const idx = siblings.findIndex((s) => s.id === item.id);
    const swapIdx = direction === "up" ? idx - 1 : idx + 1;
    if (swapIdx < 0 || swapIdx >= siblings.length) return;
    const a = siblings[idx], b = siblings[swapIdx];
    // Normalize display_orders across category to their index, then swap
    const normalized = siblings.map((s, i) => ({ id: s.id, display_order: i }));
    const rec = Object.fromEntries(normalized.map((s) => [s.id, s.display_order]));
    [rec[a.id], rec[b.id]] = [rec[b.id], rec[a.id]];
    const payload = { items: Object.entries(rec).map(([id, display_order]) => ({ id, display_order })) };
    try {
      await api.put("/menu/reorder", payload);
      load();
    } catch { toast.error("Failed to reorder"); }
  };

  // ------- Category order -------
  const openCatEditor = () => {
    setCatOrderDraft(orderedCategories);
    setCatEditorOpen(true);
  };
  const moveCat = (idx, dir) => {
    const arr = [...catOrderDraft];
    const j = dir === "up" ? idx - 1 : idx + 1;
    if (j < 0 || j >= arr.length) return;
    [arr[idx], arr[j]] = [arr[j], arr[idx]];
    setCatOrderDraft(arr);
  };
  const saveCatOrder = async () => {
    try {
      await api.put("/menu/category-order", { order: catOrderDraft });
      toast.success("Category order saved");
      setCatEditorOpen(false);
      load();
    } catch { toast.error("Failed"); }
  };

  // ------- Modifier groups delegated to ModifierGroupsEditor -------
  const setModifierGroups = (next) => setForm((f) => ({ ...f, modifier_groups: next }));

  return (
    <div className="p-8 lg:p-10">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="eyebrow mb-2">Catalog</p>
          <h1 className="font-display font-bold text-3xl tracking-tight" data-testid="admin-menu-title">Menu Items</h1>
          <p className="text-sm text-neutral-500 mt-1">{filtered.length} of {items.length} items</p>
        </div>
        <div className="flex gap-2">
          <Button
            onClick={openCatEditor}
            variant="outline"
            className="rounded-xl"
            data-testid="reorder-categories"
          ><ListOrdered className="w-4 h-4 mr-1"/>Reorder categories</Button>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={openNew} className="rounded-xl bg-[hsl(var(--primary))] hover:bg-[hsl(var(--primary))]/90" data-testid="add-menu-item"><Plus className="w-4 h-4 mr-1"/>Add Item</Button>
            </DialogTrigger>
            <DialogContent className="max-w-xl">
              <DialogHeader><DialogTitle>{editing ? "Edit Item" : "New Item"}</DialogTitle></DialogHeader>
              <div className="space-y-3 max-h-[75vh] overflow-y-auto pr-1">
                <div><Label>Name</Label><Input value={form.name} onChange={(e) => setForm({...form, name: e.target.value})} data-testid="menu-form-name"/></div>
                <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({...form, description: e.target.value})} rows={2}/></div>
                <div className="grid grid-cols-2 gap-3">
                  <div><Label>Price</Label><Input type="number" step="0.01" value={form.price} onChange={(e) => setForm({...form, price: e.target.value})} data-testid="menu-form-price"/></div>
                  <div><Label>Stock</Label><Input type="number" value={form.stock} onChange={(e) => setForm({...form, stock: e.target.value})}/></div>
                </div>
                <div><Label>Category</Label><Input value={form.category} onChange={(e) => setForm({...form, category: e.target.value})}/></div>

                {form.category === "Party Platters" && (
                  <PlatterConfigEditor
                    value={form.platter_config}
                    onChange={(platter_config) => setForm({ ...form, platter_config })}
                  />
                )}

                <div>
                  <Label>Photo</Label>
                  <div className="mt-1 flex items-start gap-3">
                    <div className="w-24 h-24 rounded-lg bg-neutral-100 border border-neutral-200 overflow-hidden shrink-0">
                      {form.image_url ? <img src={form.image_url} alt="preview" className="w-full h-full object-cover" data-testid="menu-form-image-preview"/> : <div className="w-full h-full flex items-center justify-center text-neutral-400 text-xs">No photo</div>}
                    </div>
                    <div className="flex-1 space-y-2">
                      <input ref={fileInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/gif" onChange={handleUpload} className="hidden" data-testid="menu-form-image-file"/>
                      <Button type="button" onClick={() => fileInputRef.current?.click()} disabled={uploading} variant="outline" className="rounded-lg w-full" data-testid="menu-form-upload-btn">
                        {uploading ? <><Loader2 className="w-4 h-4 mr-1 animate-spin"/>Uploading…</> : <><Upload className="w-4 h-4 mr-1"/>{form.image_url ? "Replace photo" : "Upload photo"}</>}
                      </Button>
                      <Input value={form.image_url} onChange={(e) => setForm({...form, image_url: e.target.value})} placeholder="…or paste an image URL" className="text-xs" data-testid="menu-form-image-url"/>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-3"><Switch checked={form.is_available} onCheckedChange={(v) => setForm({...form, is_available: v})}/><Label>Available</Label></div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.is_featured}
                    onCheckedChange={(v) => setForm({...form, is_featured: v})}
                    data-testid="menu-form-featured"
                  />
                  <div>
                    <Label className="flex items-center gap-1.5">
                      <Star className="w-3.5 h-3.5 text-amber-500 fill-amber-500" />
                      Show in Explore Our Favorites
                    </Label>
                    <p className="text-xs text-neutral-500">
                      This item appears on the homepage when it has a working menu photo.
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Switch
                    checked={form.contains_raw_seafood}
                    onCheckedChange={(value) => setForm({ ...form, contains_raw_seafood: value })}
                    data-testid="menu-form-raw-seafood"
                  />
                  <div>
                    <Label>Contains raw or undercooked seafood</Label>
                    <p className="text-xs text-neutral-500">
                      Shows an allergy advisory badge on the public menu.
                    </p>
                  </div>
                </div>

                {/* Modifier groups (extracted for clarity) */}
                <ModifierGroupsEditor value={form.modifier_groups} onChange={setModifierGroups}/>

                <Button onClick={save} disabled={uploading} className="w-full rounded-xl bg-[hsl(var(--primary))]" data-testid="menu-form-save">{editing ? "Update" : "Create"}</Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Category order editor */}
      <Dialog open={catEditorOpen} onOpenChange={setCatEditorOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Reorder menu categories</DialogTitle></DialogHeader>
          <p className="text-xs text-neutral-500 -mt-2 mb-2">The order below is how categories appear on the customer menu.</p>
          <div className="space-y-1.5 max-h-[60vh] overflow-y-auto">
            {catOrderDraft.map((c, i) => (
              <div key={c} className="flex items-center justify-between bg-neutral-50 border border-neutral-200 rounded-lg px-3 py-2" data-testid={`cat-order-row-${i}`}>
                <span className="font-medium text-sm">{i + 1}. {c}</span>
                <div className="flex gap-1">
                  <button onClick={() => moveCat(i, "up")} disabled={i === 0} className="p-1 disabled:opacity-30 hover:bg-neutral-200 rounded"><ArrowUp className="w-4 h-4"/></button>
                  <button onClick={() => moveCat(i, "down")} disabled={i === catOrderDraft.length - 1} className="p-1 disabled:opacity-30 hover:bg-neutral-200 rounded"><ArrowDown className="w-4 h-4"/></button>
                </div>
              </div>
            ))}
          </div>
          <Button onClick={saveCatOrder} className="w-full rounded-xl bg-[hsl(var(--primary))]" data-testid="save-category-order">Save order</Button>
        </DialogContent>
      </Dialog>

      {/* Search + category filter */}
      <div className="bg-white rounded-2xl border border-neutral-200/70 p-3 mb-5 flex items-center gap-2 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-neutral-400"/>
          <Input placeholder="Search by name, description, or category…" value={q} onChange={(e) => setQ(e.target.value)} className="pl-10 h-10 border-0 bg-neutral-50" data-testid="menu-search"/>
        </div>
        <div className="flex items-center gap-1 overflow-x-auto max-w-full">
          <button onClick={() => setCategoryFilter("")} className={`shrink-0 px-3 h-8 rounded-full text-xs font-medium ${!categoryFilter ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`} data-testid="menu-cat-filter-all">All ({items.length})</button>
          {orderedCategories.map((c) => {
            const n = items.filter((i) => i.category === c).length;
            return (
              <button key={c} onClick={() => setCategoryFilter(c)} className={`shrink-0 px-3 h-8 rounded-full text-xs font-medium ${categoryFilter === c ? "bg-neutral-900 text-white" : "bg-neutral-100 text-neutral-600 hover:bg-neutral-200"}`} data-testid={`menu-cat-filter-${c}`}>{c} ({n})</button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
        {filtered.length === 0 ? (
          <div className="col-span-full p-10 text-center text-neutral-500 bg-white rounded-2xl border border-neutral-200/70" data-testid="menu-empty">
            No items match your search.
          </div>
        ) : filtered.map((it) => (
          <div key={it.id} className="bg-white rounded-2xl border border-neutral-200/70 overflow-hidden" data-testid={`menu-item-card-${it.id}`}>
            <div className="aspect-[4/3] bg-neutral-100 relative">
              <button type="button" onClick={() => setPreview(it)} className="block w-full h-full cursor-zoom-in" aria-label={`View photo of ${it.name}`} data-testid={`admin-menu-image-${it.id}`}>
                <img src={it.image_url} alt={it.name} className="w-full h-full object-cover"/>
              </button>
              {/* Reorder buttons overlay (only visible when a specific category is selected) */}
              {categoryFilter && (
                <div className="absolute top-2 right-2 flex flex-col gap-1">
                  <button onClick={() => moveItem(it, "up")} className="w-7 h-7 rounded-full bg-white/90 backdrop-blur border border-neutral-200 shadow-sm inline-flex items-center justify-center hover:bg-white" data-testid={`move-up-${it.id}`}><ArrowUp className="w-3.5 h-3.5"/></button>
                  <button onClick={() => moveItem(it, "down")} className="w-7 h-7 rounded-full bg-white/90 backdrop-blur border border-neutral-200 shadow-sm inline-flex items-center justify-center hover:bg-white" data-testid={`move-down-${it.id}`}><ArrowDown className="w-3.5 h-3.5"/></button>
                </div>
              )}
            </div>
            <div className="p-4">
              <div className="flex items-center justify-between gap-2">
                <div className="text-xs text-neutral-500">{it.category}</div>
                {it.is_featured && (
                  <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 text-amber-700 text-[10px] font-semibold border border-amber-200" data-testid={`menu-featured-badge-${it.id}`}>
                    <Star className="w-3 h-3 fill-amber-500 text-amber-500"/>Home Favorite
                  </span>
                )}
              </div>
              <h3 className="font-display font-semibold mt-1">{it.name}</h3>
              <div className="flex items-center justify-between mt-2">
                <span className="font-bold text-[hsl(var(--primary))]">${it.price.toFixed(2)}</span>
                <span className={`text-xs ${it.is_available ? "text-emerald-600" : "text-red-600"}`}>{it.is_available ? "Available" : "Hidden"}</span>
              </div>
              {(it.modifier_groups || []).length > 0 && (
                <div className="text-[11px] text-neutral-500 mt-1">{it.modifier_groups.length} ingredient group{it.modifier_groups.length !== 1 ? "s" : ""}</div>
              )}
              {it.platter_config && (
                <div className="text-[11px] text-orange-700 mt-1" data-testid={`platter-config-badge-${it.id}`}>
                  Party platter · {it.platter_config.required_rolls} included rolls
                </div>
              )}
              <div className="flex gap-2 mt-3">
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openEdit(it)}
                  className="flex-1"
                  data-testid={`menu-edit-${it.id}`}
                >
                  <Pencil className="w-3 h-3 mr-1" />
                  Edit
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => remove(it.id)}
                  className="text-red-600 hover:text-red-700"
                  data-testid={`menu-delete-${it.id}`}
                >
                  <Trash2 className="w-3 h-3" />
                </Button>
              </div>
            </div>
          </div>
        ))}
      </div>
      {!categoryFilter && items.length > 0 && (
        <p className="mt-4 text-xs text-neutral-500 italic">Tip: pick a category above to show the up/down arrows for reordering within that category.</p>
      )}
      <ImageLightbox item={preview} onClose={() => setPreview(null)}/>
    </div>
  );
}
